import { animation, style, animate, transition, query, animateChild } from '@angular/animations';

const DEFAULT_ANIMATION_TIMING_FN = 'ease';
const DEFAULT_ANIMATION_DURATION = '250ms';
const DEFAULT_ANIMATION_PARAMS = {
    timingFn: DEFAULT_ANIMATION_TIMING_FN,
    duration: DEFAULT_ANIMATION_DURATION,
    delay: '0ms',
};

/**
 * Animation for smoothly expanding an element from height 0 to its natural height.
 * It transitions the element's height, padding, margin, and opacity from a collapsed state to an expanded state,
 * making the element smoothly grow into view.
 *
 * The `expand` animation uses `DEFAULT_ANIMATION_PARAMS` for its default parameters, which can be overridden
 * when using the animation.
 *
 * @example
 * ```ts
 * // Example usage in an Angular component
 * import { expand, collapse } from '@odx/angular/animations';
 *
 * @Component({
 *   selector: 'my-expandable-component',
 *   templateUrl: './expandable-component.html',
 *   animations: [
 *     trigger('expandCollapse', [
 *       transition('collapsed => expanded', useAnimation(expand)),
 *       transition('expanded => collapsed', useAnimation(collapse)),
 *     ])
 *   ]
 * })
 * export class ExpandableComponent {
 *   state = 'collapsed';
 *
 *   toggle(): void {
 *     this.state = this.state === 'collapsed' ? 'expanded' : 'collapsed';
 *   }
 * }
 * ```
 *
 * The animation smoothly transitions various properties (height, padding, margin, and opacity) and is controlled
 * through parameters defined in `DEFAULT_ANIMATION_PARAMS` or those provided during usage.
 */
const expand = animation([
    style({ height: 0, minHeight: 0, paddingTop: 0, paddingBottom: 0, opacity: 0, marginTop: 0, marginBottom: 0, overflow: 'hidden' }),
    animate('{{duration}} {{ delay }} {{ timingFn }}', style({ height: '*', paddingTop: '*', paddingBottom: '*', opacity: 1, marginTop: '*', marginBottom: '*' })),
], {
    params: DEFAULT_ANIMATION_PARAMS,
});
/**
 * Animation for smoothly collapsing an element from its natural height to height 0.
 * It transitions the element's height, padding, margin, and opacity from an expanded state to a collapsed state,
 * making the element smoothly shrink out of view.
 *
 * The `collapse` animation also uses `DEFAULT_ANIMATION_PARAMS` for its default parameters, which can be overridden
 * when using the animation. It's designed to be used in conjunction with the `expand` animation for creating
 * expandable/collapsible elements.
 */
const collapse = animation([
    animate('{{duration}} {{ delay }} {{ timingFn }}', style({ height: 0, minHeight: 0, paddingTop: 0, paddingBottom: 0, opacity: 0, marginTop: 0, marginBottom: 0, overflow: 'hidden' })),
], {
    params: DEFAULT_ANIMATION_PARAMS,
});

/**
 * Generates a fadeIn animation with customizable opacity target.
 * This animation gradually changes the element's opacity from 0 to a specified value, creating a "fade in" visual effect.
 *
 * @param {number} to - The final opacity value of the element at the end of the animation. Defaults to 1 (fully opaque).
 * @returns {AnimationReferenceMetadata} An Angular animation object that can be used with Angular's animation system.
 *
 * @example
 * ```ts
 * // Example usage in an Angular component with a fadeIn animation
 * import { fadeIn } from '@odx/angular/animations';
 * import { trigger, transition, useAnimation } from '@angular/animations';
 *
 * @Component({
 *   selector: 'app-fade-in-example',
 *   template: `
 *      @if(isVisible) {
 *       <div @fadeInAnimation>Fade In Content</div>
 *      }
 *       <button (click)="showContent()">Show Content</button>`,
 *   animations: [
 *     trigger('fadeInAnimation', [
 *       transition(':enter', useAnimation(fadeIn(), { params: { duration: '500ms' } }))
 *     ])
 *   ]
 * })
 * export class FadeInExampleComponent {
 *   isVisible = false;
 *
 *   showContent(): void {
 *     this.isVisible = true;
 *   }
 * }
 * ```
 *
 * This example demonstrates how to apply the fadeIn animation to an element, making it smoothly appear on the screen.
 */
const fadeIn = (to = 1) => animation([style({ opacity: 0 }), animate('{{duration}} {{ delay }} {{ timingFn }}', style({ opacity: to }))], {
    params: DEFAULT_ANIMATION_PARAMS,
});
/**
 * Generates a fadeOut animation with customizable opacity target.
 * This animation gradually changes the element's opacity from its current value to a specified value, creating a "fade out" visual effect.
 *
 * @param {number} to - The target opacity value of the element at the end of the animation. Defaults to 0 (fully transparent).
 * @returns {AnimationReferenceMetadata} An Angular animation object that can be used with Angular's animation system.
 *
 * @example
 * ```ts
 * // Example usage in an Angular component with a fadeOut animation
 * import { fadeOut } from '@odx/angular/animations';
 * import { trigger, transition, useAnimation } from '@angular/animations';
 *
 * @Component({
 *   selector: 'app-fade-out-example',
 *   template: `
 *      @if(isVisible) {
 *      <div @fadeOutAnimation>Fade Out Content</div>
 *      }
 *      <button (click)="hideContent()">Hide Content</button>`,
 *   animations: [
 *     trigger('fadeOutAnimation', [
 *       transition(':leave', useAnimation(fadeOut(), { params: { duration: '500ms' } }))
 *     ])
 *   ]
 * })
 * export class FadeOutExampleComponent {
 *   isVisible = true;
 *
 *   hideContent(): void {
 *     this.isVisible = false;
 *   }
 * }
 * ```
 *
 * This example demonstrates how to apply the fadeOut animation to an element, making it smoothly disappear from the screen.
 */
const fadeOut = (to = 0) => animation([animate('{{duration}} {{ delay }} {{ timingFn }}', style({ opacity: to }))], {
    params: DEFAULT_ANIMATION_PARAMS,
});

function createSlideInAnimation(from, direction) {
    return animation([
        style({ transform: `translate${direction}({{ from }})` }),
        animate('{{duration}} {{ delay }} {{ timingFn }}', style({ transform: `translate${direction}(0)` })),
    ], {
        params: {
            ...DEFAULT_ANIMATION_PARAMS,
            from,
        },
    });
}
function createSlideOutAnimation(to, direction) {
    return animation([animate('{{duration}} {{ delay }} {{ timingFn }}', style({ transform: `translate${direction}({{ to }})` }))], {
        params: {
            ...DEFAULT_ANIMATION_PARAMS,
            to,
        },
    });
}
/**
 * Utility functions for creating slide-in and slide-out animations in specific directions.
 * These include slideInX, slideInY, slideInDown, slideInUp, slideInLeft, slideInRight,
 * slideOutX, slideOutY, slideOutDown, slideOutUp, slideOutLeft, and slideOutRight.
 * Each function is configured for a specific slide direction and distance, making it easy to apply
 * consistent slide animations across your application.
 *
 * @example
 * ```ts
 * // Using slideInLeft and slideOutRight in a component
 * animations: [
 *   trigger('slideInOut', [
 *     transition(':enter', [useAnimation(slideInLeft)]),
 *     transition(':leave', [useAnimation(slideOutRight)])
 *   ])
 * ]
 * ```
 *
 * Note: The 'from' and 'to' parameters for slideIn and slideOut functions respectively
 * can be customized for different slide distances.
 */
const slideInX = (from) => createSlideInAnimation(from, 'X');
const slideInY = (from) => createSlideInAnimation(from, 'Y');
const slideInDown = slideInY('-100%');
const slideInUp = slideInY('100%');
const slideInLeft = slideInX('-100%');
const slideInRight = slideInX('100%');
const slideOutX = (to) => createSlideOutAnimation(to, 'X');
const slideOutY = (to) => createSlideOutAnimation(to, 'Y');
const slideOutDown = slideOutY('100%');
const slideOutUp = slideOutY('-100%');
const slideOutLeft = slideOutX('-100%');
const slideOutRight = slideOutX('100%');

/**
 * Utility animation that waits for animations on child components to complete.
 * This animation should be used within parent components that have child components with their
 * own animations. It ensures that the parent's enter and leave animations are only triggered
 * after all child animations have completed, creating a seamless animation sequence.
 *
 * This utility uses Angular's animation functions `query` and `animateChild` to find and animate
 * child elements marked with Angular's animation triggers. The `{ optional: true }` parameter
 * ensures that the animation gracefully handles cases where no child animations are defined.
 *
 * @example
 * ```ts
 * // Using waitForChildAnimations in a parent component with children that have animations
 * import { trigger, transition, style, animate, query, animateChild } from '@angular/animations';
 * import { waitForChildAnimations } from '@odx/angular/animations';
 *
 * @Component({
 *   selector: 'app-parent',
 *   templateUrl: './parent.component.html',
 *   animations: [
 *     trigger('parentAnimation', [
 *       // Define parent animations here
 *       transition(':enter', [style({ opacity: 0 }), animate('0.5s ease-in', style({ opacity: 1 }))]),
 *       transition(':leave', [animate('0.5s ease-out', style({ opacity: 0 }))]),
 *       // Use waitForChildAnimations for coordinating with child animations
 *       waitForChildAnimations
 *     ])
 *   ]
 * })
 * export class ParentComponent {
 *   // Component logic here
 * }
 * ```
 *
 * Note: The `waitForChildAnimations` utility is added to the parent component's animation triggers,
 * ensuring that any child component animations are completed before continuing with the parent's
 * own animations.
 */
const waitForChildAnimations = transition(':enter, :leave', [query('@*', animateChild(), { optional: true })]);

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_ANIMATION_DURATION, DEFAULT_ANIMATION_PARAMS, DEFAULT_ANIMATION_TIMING_FN, collapse, expand, fadeIn, fadeOut, slideInDown, slideInLeft, slideInRight, slideInUp, slideInX, slideInY, slideOutDown, slideOutLeft, slideOutRight, slideOutUp, slideOutX, slideOutY, waitForChildAnimations };
